// @ts-check
import AppRouter from 'common/AppRouter';
import { ONBOARDING_ERRORS, TRACKING } from 'common/constants/constants';
import { fetchGitUserDetails } from 'common/services/repositories.service';
import { logEvent } from 'common/services/tracking.service';
import { isProviderWithOrgs } from 'common/utils/git-providers.utils';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { deleteSensorAuthorization } from 'state/callers';

/**
 * Tracks git setup failure analytics event
 * @param {string} email
 * @param {string} selectedProvider
 * @param {string} error
 */
function trackVerificationFailure(email, selectedProvider, error) {
  logEvent(TRACKING.EVENTS.SETUP_WIZARD_CONNECT_GIT_FAIL, {
    identity_email: email,
    git_provider: selectedProvider,
    auth: 'oauth',
    reason: error
  });
}

/**
 * Verifies that provider account has associated organizations
 * @param {object} props
 * @param {any[]} props.gitSensorAuthorizations
 * @param {{ email:string; }} props.account
 * @returns {JSX.Element}
 */
export const VerifyProvider = ({ gitSensorAuthorizations = [], account }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    /**
     * Checks if user has organizations.
     * If not, will delete the sensor and redirect back to provider selection page
     */
    async function checkUserOrganizations() {
      const sensorAuthorizationId = gitSensorAuthorizations?.[0]?.id;

      if (!sensorAuthorizationId) {
        return;
      }

      const cleanup = () =>
        deleteSensorAuthorization(sensorAuthorizationId)(dispatch);

      const sensorName = gitSensorAuthorizations?.[0]?.sensor_name;

      const isProviderTypeWithOrganization = isProviderWithOrgs(sensorName);

      if (!isProviderTypeWithOrganization) {
        history.replace(AppRouter.ONBOARDING.path, {
          providerOrganizationsVerified: true
        });
        return;
      }
      try {
        const sensorDetails = await fetchGitUserDetails(sensorAuthorizationId);

        // @ts-expect-error
        const { orgs } = sensorDetails;

        if (orgs?.length) {
          history.replace(AppRouter.ONBOARDING.path, {
            providerOrganizationsVerified: true
          });

          return;
        }

        await cleanup();

        trackVerificationFailure(
          account.email,
          sensorName,
          ONBOARDING_ERRORS.NO_ORGANIZATIONS
        );

        history.replace(AppRouter.ONBOARDING.path, {
          error: ONBOARDING_ERRORS.NO_ORGANIZATIONS,
          providerOrganizationsVerified: true
        });
      } catch (error) {
        trackVerificationFailure(account.email, sensorName, error.message);

        history.replace(AppRouter.ONBOARDING.path, {
          error: ONBOARDING_ERRORS.NO_ORGANIZATIONS,
          providerOrganizationsVerified: true
        });
      }
    }

    checkUserOrganizations();
    // We only need this hook to run in case the sensor changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gitSensorAuthorizations]);

  return null;
};
